import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { AuthService } from "./service/auth-service";
import { Hub, Logger } from '@aws-amplify/core';
import { Auth } from "aws-amplify";
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Alert, Box, InputAdornment } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { ButtonWithEffects } from '../../components/ui/ButtonWithEffects';

const containerSx = { minHeight: '100vh', background: 'linear-gradient(135deg, #F8FAFC 0%, #E8EDF2 100%)' };
const imageSx = {
    background: 'linear-gradient(135deg, #1F2F42 0%, #3A5068 50%, #C89E57 100%)',
    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 6,
    '& *': { color: '#fff' },
};
const authBoxSx = {
    width: '100%', maxWidth: 440, p: 6,
    background: 'rgba(255,255,255,0.9)', borderRadius: 4,
    boxShadow: '0 20px 60px rgba(0,0,0,0.08)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.5)',
};

export function LoginForm() {
    const logger = new Logger('LoginForm');
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState("");
    const [userNotConfirmed, setUserNotConfirmed] = useState(false);
    const [email, setEmail] = useState(localStorage.getItem('savedEmail') || '');
    const [password, setPassword] = useState('');

    useEffect(() => {
        const onHubCapsule = async ({channel, payload}) => {
            if (channel === AuthService.CHANNEL && payload.event === AuthService.AUTH_EVENTS.LOGIN) {
                if (!payload.success) {
                    if (payload.error.code === 'UserNotConfirmedException') {
                        setUserNotConfirmed(true);
                        await AuthService.resendConfirmationCode(payload.email);
                    } else {
                        setErrorMessage(payload.message);
                    }
                } else {
                    history.push("/");
                }
            }
        };
        Hub.listen(AuthService.CHANNEL, onHubCapsule);
        Auth.currentAuthenticatedUser().then(() => history.push("/")).catch(() => {});
        return () => Hub.remove(AuthService.CHANNEL, onHubCapsule);
    }, []);

    const onFinish = async (e) => { e.preventDefault(); await AuthService.login(email, password); };

    return (
        <Grid container component="main" sx={containerSx}>
            <Grid item xs={false} sm={4} md={7} sx={imageSx}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>assetsMaven</Typography>
                <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9, textAlign: 'center', mb: 4 }}>
                    Receipts. Expenses. Peace of mind.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: 0.85 }}>
                    {['Auto-categorize spending', 'Track subscriptions', 'Export tax-ready CSV'].map(t => (
                        <Typography key={t} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>✓ {t}</Typography>
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} sm={8} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.5}}>
                    <Box sx={authBoxSx}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <img src={window.location.origin + '/logos/logo-md.png'} style={{width: 160}} alt=""/>
                        </Box>
                        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
                            Welcome Back
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 3 }}>
                            Sign in to manage your finances
                        </Typography>

                        {errorMessage && (
                            <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}}>
                                <Alert severity="error" sx={{mb: 2}}>{errorMessage}</Alert>
                            </motion.div>
                        )}
                        {userNotConfirmed && (
                            <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}}>
                                <Alert severity="warning" sx={{mb: 2}}>
                                    Please <Link to="/registerconfirm">confirm your email</Link> first.
                                </Alert>
                            </motion.div>
                        )}

                        <form onSubmit={onFinish} noValidate>
                            <TextField variant="outlined" margin="normal" required fullWidth
                                label="Email Address" autoComplete="email" autoFocus
                                value={email} onInput={e => setEmail(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="primary"/></InputAdornment> }}
                            />
                            <TextField variant="outlined" margin="normal" required fullWidth
                                label="Password" type="password" autoComplete="current-password"
                                value={password} onInput={e => setPassword(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary"/></InputAdornment> }}
                            />
                            <FormControlLabel
                                control={<Checkbox value="remember" color="primary" sx={{ borderRadius: '6px' }}/>}
                                label={<Typography variant="body2" color="text.secondary">Remember me</Typography>}
                            />
                            <ButtonWithEffects type="submit" fullWidth sx={{ mt: 2, mb: 2 }}>
                                Sign In
                            </ButtonWithEffects>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Link to="/forgotpassword1" style={{ fontSize: '0.875rem' }}>Forgot password?</Link>
                                <Link to="/register" style={{ fontSize: '0.875rem' }}>Create an account</Link>
                            </Box>
                        </form>
                    </Box>
                </motion.div>
            </Grid>
        </Grid>
    );
}
